import './InsightsPage.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import React, { useMemo } from 'react';
import Title from '../../shared/components/title/Title';
import { useTranslation } from 'react-i18next';
import { PROJECTS } from '../../shared/routes/path';
import FilterPanel from '../../shared/components/filter-panel/FilterPanel';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import useProjectNavigate from '../../shared/hooks/projects/preview/UseProjectNavigate';
import { ConfirmDialog } from 'primereact/confirmdialog';
import ChartWithAnomalies from './components/anomalies/ChartWithAnomalies';
import { MonitorEventsResponse } from '../../shared/models/monitor/MonitorEventsResponse';
import TopSources from './components/top-sources/TopSources';
import ContextInsights from './components/context-insights/ContextInsights';
import OverallInsights from './components/overall-insights/OverallInsights';
import SourcesInsights from './components/sources-insights/SourcesInsights';
import QuestionsEvaluation from './components/questions-evaluation/QuestionsEvaluation';
import {
  ContextInsightsType,
  SentimentType,
} from './components/context-insights/types/ContextInsightsType';
import { QuestionEvaluationType } from './components/questions-evaluation/types/QuestionEvaluationType';
import { MonitorType } from '../../shared/types/monitor/MonitorType';

const InsightsPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const { projectId } = useParams();
  // const { selectedChatType, monitorPeriod } = useFilters();
  // const { startDate, endDate } = useProjectDates();

  const navigate = useNavigate();

  const reportData = {
    anomalies: [
      {
        date: '2025-11-25',
        model: 'GPT',
        description: {
          en: "The sudden rise in Shoper's ranking position by 3 places in the GPT model is unusual and may indicate a change in the visibility assessment algorithm.",
          pl: 'Nagły wzrost pozycji marki Shoper w rankingu o 3 miejsca w modelu GPT jest nietypowy i może wskazywać na zmianę w algorytmie oceny widoczności.',
        },
      },
      {
        date: '2025-11-25',
        model: 'Gemini',
        description: {
          en: "Shoper's sudden dominance in the Gemini model with a 40% share of voice may suggest an overestimation of its importance by the algorithm.",
          pl: 'Nagła dominacja marki Shoper w modelu Gemini z udziałem głosu wynoszącym 40% może sugerować przeszacowanie jej znaczenia przez algorytm.',
        },
      },
    ],
    top_prompts: [
      {
        rank: 1,
        prompt: 'Jakie są alternatywy dla Sky-Shop?',
        description: {
          en: 'The prompt is highly popular, which may indicate growing interest in alternatives to Sky-Shop.',
          pl: 'Prompt cieszy się dużym zainteresowaniem, co może świadczyć o rosnącym zainteresowaniu alternatywami dla Sky-Shop.',
        },
      },
      {
        rank: 2,
        prompt: 'Jakie są alternatywy dla Sky-Shop?',
        description: {
          en: 'The prompt is highly popular, which may indicate growing interest in alternatives to Sky-Shop.',
          pl: 'Prompt cieszy się dużym zainteresowaniem, co może świadczyć o rosnącym zainteresowaniu alternatywami dla Sky-Shop.',
        },
      },
      {
        rank: 3,
        prompt: 'Jakie są alternatywy dla Sky-Shop?',
        description: {
          en: 'The prompt is highly popular, which may indicate growing interest in alternatives to Sky-Shop.',
          pl: 'Prompt cieszy się dużym zainteresowaniem, co może świadczyć o rosnącym zainteresowaniu alternatywami dla Sky-Shop.',
        },
      },
      {
        rank: 4,
        prompt: 'Jakie są alternatywy dla Sky-Shop?',
        description: {
          en: 'The prompt is highly popular, which may indicate growing interest in alternatives to Sky-Shop.',
          pl: 'Prompt cieszy się dużym zainteresowaniem, co może świadczyć o rosnącym zainteresowaniu alternatywami dla Sky-Shop.',
        },
      },
    ],
    top_sources: [],
    context_insights: {
      brand_strengths: {
        en: "Shoper's strengths include a wide range of products and a strong position in the e-commerce market.",
        pl: 'Mocne strony marki Shoper obejmują szeroką ofertę produktów i silną pozycję na rynku e-commerce.',
      },
      share_of_answer: {
        en: 'The Shoper brand has a high share of answers, indicating significant visibility in the analyzed data.',
        pl: 'Marka Shoper ma wysoki udział w odpowiedziach, co wskazuje na jej znaczną widoczność w analizowanych danych.',
      },
      sentiment_analysis: {
        type: 'POSITIVE',
        justification: {
          en: 'Responses related to the Shoper brand are generally positive, highlighting its strong market position and innovation.',
          pl: 'Odpowiedzi związane z marką Shoper są generalnie pozytywne, podkreślając jej silną pozycję rynkową i innowacyjność.',
        },
      },
      topics_with_sources: {
        en: 'Topics related to the Shoper brand are well supported by diverse sources, enhancing their credibility.',
        pl: 'Tematy związane z marką Shoper są dobrze wspierane przez różnorodne źródła, co wzmacnia ich wiarygodność.',
      },
      brand_attributes_per_llm: {
        en: 'The Shoper brand is perceived as a leader in its segment, with positive attributes regarding competitiveness and innovation.',
        pl: 'Marka Shoper jest postrzegana jako lider w swoim segmencie, z pozytywnymi atrybutami dotyczącymi konkurencyjności i innowacyjności.',
      },
    },
    overall_insights: [
      {
        en: 'The visibility of the Shoper brand is stable, and its position is relatively high compared to other brands.',
        pl: 'Widoczność marki Shoper jest stabilna, a jej pozycja jest stosunkowo wysoka w zestawieniu z innymi markami.',
      },
      {
        en: 'No new challengers for the Shoper brand appeared during the analyzed period.',
        pl: 'Nowi konkurenci dla marki Shoper nie pojawili się w analizowanym okresie.',
      },
      {
        en: 'Discrepancies in results between different LLM models are minimal, indicating consistency in data interpretation.',
        pl: 'Rozbieżności w wynikach pomiędzy różnymi modelami LLM są minimalne, co wskazuje na spójność w interpretacji danych.',
      },
      {
        en: 'The consistency index is high, which means that the data is interpreted uniformly across different models.',
        pl: 'Index spójności jest wysoki, co oznacza, że dane są interpretowane w sposób jednolity przez różne modele.',
      },
    ],
    sources_insights: [
      {
        en: 'The domain share of the Shoper brand over time is stable, without significant fluctuations.',
        pl: 'Udział domeny marki Shoper w czasie jest stabilny, bez znaczących fluktuacji.',
      },
      {
        en: 'The source momentum does not show significant changes, indicating no dominance of new sources.',
        pl: 'Momentum źródeł nie wykazuje istotnych zmian, co oznacza brak dominacji nowych źródeł.',
      },
      {
        en: 'The influence of sources on visibility is evenly distributed, suggesting no dominance of a single source.',
        pl: 'Wpływ źródeł na widoczność jest równomiernie rozłożony, co sugeruje brak dominacji jednego źródła.',
      },
      {
        en: 'Universal and niche sources are used to a similar extent, indicating a diverse content distribution.',
        pl: 'Źródła uniwersalne i niszowe są wykorzystywane w podobnym stopniu, co wskazuje na zróżnicowaną dystrybucję treści.',
      },
    ],
    questions_evaluation: [
      {
        status: 'ACTIVE',
        analysis: {
          en: 'The monitor is active and functioning correctly, no intervention required.',
          pl: 'Monitor jest aktywny i wykazuje poprawne działanie, nie wymaga interwencji.',
        },
        reasoning: {
          en: "No noticeable irregularities in the monitor's operation, suggesting its stability.",
          pl: 'Brak zauważalnych nieprawidłowości w działaniu monitora, co sugeruje jego stabilność.',
        },
        should_be: 'ACTIVE',
        monitor_id: '16524',
      },
    ],
  };

  const anomalies: MonitorEventsResponse[] | null = useMemo(() => {
    if (!reportData) {
      return null;
    }

    return reportData.anomalies.map(
      (data) =>
        ({
          created: new Date(data.date),
          type: 'ANOMALY',
          label: data.description['en'] ?? '',
          iconClass: 'ico--info',
          model: {
            iconClass: 'ico--bot',
            label: data.model.toUpperCase(),
          },
        } as MonitorEventsResponse)
    );
  }, [reportData]);

  const topPrompts: { rank: number; prompt: string; description: string }[] | null = useMemo(() => {
    if (!reportData) {
      return null;
    }

    return reportData.top_prompts.map((data) => ({
      rank: data.rank,
      prompt: data.prompt,
      description: data.description['en'] ?? '',
    }));
  }, [reportData]);

  const topSources: any[] | null = useMemo(() => {
    if (!reportData) {
      return null;
    }

    return [];

    // return topSources.top_prompts.map((data) => ({
    //   rank: data.rank,
    //   prompt: data.prompt,
    //   description: data.description['en'] ?? '',
    // }));
  }, [reportData]);

  const contextInsights: ContextInsightsType | null = useMemo(() => {
    if (!reportData) {
      return null;
    }

    const data = reportData.context_insights;
    return {
      brandStrengths: data.brand_strengths['en'],
      shareOfAnswer: data.share_of_answer['en'],
      sentimentAnalysis: {
        type: data.sentiment_analysis.type as SentimentType,
        justification: data.sentiment_analysis.justification['en'],
      },
      topicsWithSources: data.topics_with_sources['en'],
      brandAttributesPerLlm: data.brand_attributes_per_llm['en'],
    };
  }, [reportData]);

  const sourcesInsights: string[] | null = useMemo(() => {
    if (!reportData) {
      return null;
    }

    return reportData.sources_insights.map((data) => data['en']);
  }, [reportData]);

  const overallInsights: string[] | null = useMemo(() => {
    if (!reportData) {
      return null;
    }

    return reportData.overall_insights.map((data) => data['en']);
  }, [reportData]);

  const questionsEvaluation: QuestionEvaluationType[] | null = useMemo(() => {
    if (!reportData) {
      return null;
    }

    return reportData.questions_evaluation.map((data) => ({
      analysis: data.analysis['en'],
      status: data.should_be as MonitorType,
      reasoning: data.reasoning['en'],
      monitorId: Number(data.monitor_id),
      shouldBe: data.should_be as MonitorType,
    }));
  }, [reportData]);

  const projectIdNumber = Number(projectId);
  const { accountResponse } = useAccount();

  useProjectNavigate(projectIdNumber);

  if (!accountResponse || !accountResponse.addons.includes('CONTEXT_INSIGHTS') || !projectId) {
    navigate(PROJECTS);
    return <></>;
  }

  return (
    <>
      <ConfirmDialog className="dialog-box" />
      <div className="insights-page page-content">
        <div className="page-box">
          <div className="page-box__filters" data-observe-sticky>
            <Title
              isBaseCategory={false}
              title={t('insights')}
              children={
                <div className="filters">
                  <FilterPanel baseUrl={location.pathname} />
                </div>
              }
              showStatusDot={true}
            />
          </div>
          <div className="page-box__stats">
            <div className="stats">
              <ChartWithAnomalies anomalies={anomalies} topPrompts={topPrompts} />
            </div>
            <TopSources topSources={topSources} />
            <div className="stats stats--two-cols">
              <SourcesInsights sourcesInsights={sourcesInsights} />
              <OverallInsights overallInsights={overallInsights} />
            </div>
            <ContextInsights contextInsights={contextInsights} />
            <QuestionsEvaluation questionsEvaluation={questionsEvaluation} />
          </div>
        </div>
      </div>
    </>
  );
};

export default InsightsPage;
